import React, { useMemo } from 'react';

import styled, { ThemeProvider } from 'styled-components';

import { connectIntlAuth, IAuthMapStateToProps, IConnectedProps } from './hooks/connectAuth';

import THEMES from '../../styledComponents/Common/Themes/Themes';

import { Steps } from './Constants';
import AuthError from './Main/AuthError';
import AuthMaintenance from './Main/AuthMaintenance';
import AuthTitle from './Main/AuthTitle';
import Login from './Steps/Login/Login';
import Payment from './Steps/Payment/Payment';
import SignUp from './Steps/SignUp/SignUp';
import { IAuthState } from './Store/Reducers';

interface IAuthFunctionalProps {
    step: string,
    theme: string,
    maintenance: boolean,
    containerColor?: string
}

export type TAuthFunctionalProps = IConnectedProps<IAuthFunctionalProps>;

const Container = styled.div<{ $containerColor?: string }>`
    z-index: 2;
    width: 100vw;
    display: flex;
    position: relative;
    align-items: center;
    max-width: 24.375em;
    box-sizing: border-box;
    flex-direction: column;
    justify-content: center;
    padding: ${({ theme }) => theme.space.mouse};
    border-radius: ${({ theme }) => theme.radii[2]};
    font-size: ${({ theme }) => theme.fontSizes.mouse};
    background-color: ${({ theme, $containerColor }) => $containerColor || theme.colors.backgroundVariant};

    .signup__terms-and-policy-informative {
        font-size: 16px;
        text-align: center;

        > p {
            margin: 0;
            font-size: 0.875em;
            line-height: 1.428571429;
            color: ${({ theme }) => theme.colors.gray700};

            > a {
                color: ${({ theme }) => theme.colors.purple600};
            }
        }
    }
`;

const AuthFunctional: React.FC<TAuthFunctionalProps> = ({ intl, dispatch, ...props }) => {
    const { step, theme, maintenance, containerColor } = props;

    const astroTheme = theme === 'light' ? THEMES.light : THEMES.dark;

    const currentStep = useMemo(() => {
        if (step === Steps.LOGIN) return <Login />;

        if (step === Steps.PAYMENT) return <Payment />;

        return <SignUp />;
    }, [step]);

    const renderAuth = () => (
        <Container $containerColor={containerColor} data-cy="auth-functional-container">
            <AuthError />

            <AuthTitle />

            {currentStep}
        </Container>
    );

    return (
        <ThemeProvider theme={astroTheme}>
            {!maintenance ? renderAuth() : <AuthMaintenance />}
        </ThemeProvider>
    );
};

const mapStateToProps: IAuthMapStateToProps<IAuthFunctionalProps> = (state, isComparison = false) => (!isComparison) ?
    {
        step: (state as IAuthState).Container.step,
        theme: (state as IAuthState).Container.theme,
        maintenance: (state as IAuthState).Container.maintenance,
        containerColor: (state as IAuthState).Container.containerColor
    } : {
        step: state.step,
        theme: state.theme,
        maintenance: state.maintenance,
        containerColor: state.containerColor
    };

export default connectIntlAuth(mapStateToProps)(AuthFunctional);
